import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
export interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  count?: number;
}
export interface TabsProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  variant?: 'default' | 'pills';
  size?: 'sm' | 'md';
  className?: string;
}
export function Tabs({
  tabs,
  activeTab,
  onTabChange,
  variant = 'default',
  size = 'md',
  className = ''
}: TabsProps) {
  const [indicatorStyle, setIndicatorStyle] = useState({
    left: 0,
    width: 0
  });
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  // Update indicator position
  useEffect(() => {
    const activeTabElement = tabRefs.current.get(activeTab);
    if (activeTabElement && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const tabRect = activeTabElement.getBoundingClientRect();
      setIndicatorStyle({
        left: tabRect.left - containerRect.left,
        width: tabRect.width
      });
    }
  }, [activeTab, tabs]);
  const sizeStyles = {
    sm: 'text-body-sm py-2 px-3',
    md: 'text-body-md py-2.5 px-4'
  };
  if (variant === 'pills') {
    return <div ref={containerRef} role="tablist" className={`inline-flex bg-surface-100 rounded-lg p-1 ${className}`} data-id="element-579">
        {tabs.map(tab => <button key={tab.id} ref={el => {
        if (el) tabRefs.current.set(tab.id, el);
      }} role="tab" aria-selected={activeTab === tab.id} onClick={() => onTabChange(tab.id)} className={`
              relative flex items-center gap-2
              ${sizeStyles[size]}
              font-medium rounded-md
              transition-colors duration-150
              ${activeTab === tab.id ? 'text-surface-900' : 'text-surface-500 hover:text-surface-700'}
            `} data-id="element-580">
            {activeTab === tab.id && <motion.div layoutId="pill-indicator" className="absolute inset-0 bg-white rounded-md shadow-sm" transition={{
          type: 'spring',
          stiffness: 500,
          damping: 35
        }} data-id="element-581" />}
            <span className="relative z-10 flex items-center gap-2" data-id="element-582">
              {tab.icon}
              {tab.label}
              {tab.count !== undefined && <span className={`
                    px-1.5 py-0.5 text-caption rounded-full
                    ${activeTab === tab.id ? 'bg-primary-100 text-primary-700' : 'bg-surface-200 text-surface-600'}
                  `} data-id="element-583">
                  {tab.count}
                </span>}
            </span>
          </button>)}
      </div>;
  }
  return <div ref={containerRef} role="tablist" className={`relative border-b border-surface-200 ${className}`} data-id="element-584">
      <div className="flex" data-id="element-585">
        {tabs.map(tab => <button key={tab.id} ref={el => {
        if (el) tabRefs.current.set(tab.id, el);
      }} role="tab" aria-selected={activeTab === tab.id} onClick={() => onTabChange(tab.id)} className={`
              flex items-center gap-2
              ${sizeStyles[size]}
              font-medium
              transition-colors duration-150
              ${activeTab === tab.id ? 'text-primary-600' : 'text-surface-500 hover:text-surface-700'}
            `} data-id="element-586">
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && <span className={`
                  px-1.5 py-0.5 text-caption rounded-full
                  ${activeTab === tab.id ? 'bg-primary-100 text-primary-700' : 'bg-surface-100 text-surface-600'}
                `} data-id="element-587">
                {tab.count}
              </span>}
          </button>)}
      </div>
      {/* Sliding indicator */}
      <motion.div className="absolute bottom-0 h-0.5 bg-primary-500" initial={false} animate={{
      left: indicatorStyle.left,
      width: indicatorStyle.width
    }} transition={{
      type: 'spring',
      stiffness: 500,
      damping: 35
    }} data-id="element-588" />
    </div>;
}
// Tab Panel component
export interface TabPanelProps {
  id: string;
  activeTab: string;
  children: React.ReactNode;
  className?: string;
}
export function TabPanel({
  id,
  activeTab,
  children,
  className = ''
}: TabPanelProps) {
  if (id !== activeTab) return null;
  return <motion.div role="tabpanel" aria-labelledby={id} initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.2
  }} className={className} data-id="element-589">
      {children}
    </motion.div>;
}